import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import {
  ActionIcon,
  Anchor,
  Button,
  Checkbox,
  Flex,
  Group,
  Loader,
  Text,
  Title,
  Tooltip
} from "@mantine/core";
import { IconDownload, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { DataTable } from "@/components/DataTable";
import { type Document } from "@/models";
import { getDocumentsListForProspectQuery, useApi } from "@/api";
import { downloadDocumentFile } from "@/api/documents/downloadFile";
import { toggleDocumentLivingStoryInclusion } from "@/api/documents/toggleLivingStoryInclusion";
import { openBlobInNewTabFromUserGesture } from "@/utils/openBlobInNewTabFromUserGesture";
import {
  canAddOrEditProspectDocuments,
  canViewProspectDocuments
} from "@/security/logic";
import { showError, showSuccess } from "@/notifications/Notifications";

export const Route = createFileRoute("/prospects/$prospectId/documents")({
  component: DocumentsListComponent,
  loader: () => {
    return {
      crumb: "Documents & Notes"
    };
  }
});

function DocumentsListComponent() {
  const { prospectId } = Route.useParams();
  const { secureAxios } = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { prospect, userClaim } = Route.useRouteContext();

  const hasViewPermission = canViewProspectDocuments(
    userClaim,
    prospect?.communityId ?? 0
  );
  const hasEditPermission = canAddOrEditProspectDocuments(
    userClaim,
    prospect?.communityId ?? 0
  );

  // Fetch documents only if the user has permission to view them; otherwise, defaults to []
  const { data: documents = [], isPending } = useQuery({
    ...getDocumentsListForProspectQuery(secureAxios, Number(prospectId)),
    enabled: hasViewPermission
  });

  const toggleLivingStoryMutation = useMutation<
    boolean,
    Error,
    { documentId: string; includeDetailsInLivingStory: boolean }
  >({
    mutationFn: async ({ documentId, includeDetailsInLivingStory }) => {
      return await toggleDocumentLivingStoryInclusion(
        secureAxios,
        documentId,
        includeDetailsInLivingStory
      );
    },
    onSuccess: (updatedValue, variables) => {
      queryClient.setQueryData<Document[]>(
        ["documents", "prospect", Number(prospectId)],
        (oldData) => {
          if (!oldData) {
            return oldData;
          }
          return oldData.map((doc) =>
            doc.id.toString() === variables.documentId
              ? { ...doc, includeDetailsInLivingStory: updatedValue }
              : doc
          );
        }
      );
      showSuccess("Living story inclusion updated successfully", "Saved");
    },
    onError: (error) => {
      showError("Failed to update living story inclusion. Please try again.");
      console.error("Failed to update living story inclusion:", error);
    }
  });

  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleCreateClick = () => {
    void navigate({
      to: "/prospects/$prospectId/documents/new",
      params: { prospectId }
    });
  };

  const handleTitleClick = (documentId: number) => {
    void navigate({
      to: "/prospects/$prospectId/documents/$documentId",
      params: { prospectId, documentId: documentId.toString() }
    });
  };

  const columnHelper = createColumnHelper<Document>();

  const handleDownload = async (doc: Document) => {
    if (!doc.fileName) {
      return;
    }
    setDownloadingId(doc.id);
    try {
      await openBlobInNewTabFromUserGesture(
        () => downloadDocumentFile(secureAxios, doc.id.toString()),
        { fileName: doc.fileName }
      );
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <>
      <Flex justify={"space-between"} align="center" mb="md">
        <Title order={5}>Documents & Notes</Title>
        <Tooltip
          disabled={hasEditPermission}
          label={
            hasEditPermission
              ? undefined
              : "You do not have access to create a document or note."
          }
        >
          <Button
            onClick={handleCreateClick}
            leftSection={<IconPlus size={18} />}
            disabled={!hasEditPermission}
            color="blue"
          >
            Create Document or Note
          </Button>
        </Tooltip>
      </Flex>

      <DataTable
        data={documents}
        isLoading={hasViewPermission && isPending}
        emptyText={
          hasViewPermission
            ? "No documents or notes on this prospect."
            : "You don't have access to this prospect's documents and notes. Contact your community administrator to request permission."
        }
        columns={[
          columnHelper.accessor("title", {
            header: "Title",
            cell: ({ getValue, row }) => (
              <Anchor
                component="button"
                type="button"
                onClick={() => handleTitleClick(row.original.id)}
              >
                {getValue()}
              </Anchor>
            )
          }),
          columnHelper.accessor("createdAt", {
            header: "Date Created",
            cell: ({ getValue }) =>
              DateTime.fromISO(getValue()).toFormat("yyyy-MM-dd")
          }),
          columnHelper.accessor("createdByUser", {
            header: "Uploaded By",
            cell: ({ getValue }) => getValue()?.displayName ?? ""
          }),
          columnHelper.accessor("source", {
            header: "Source",
            cell: ({ getValue }) => getValue().displayName
          }),
          columnHelper.accessor("type", {
            header: "Type",
            cell: ({ getValue }) => getValue().displayName
          }),
          columnHelper.accessor("includeDetailsInLivingStory", {
            header: "Living Story",
            cell: ({ getValue, row }) => (
              <Group gap={9}>
                <Checkbox
                  size="xs"
                  checked={getValue<boolean>()}
                  disabled={
                    !row.original.detailsHtml ||
                    !hasEditPermission ||
                    toggleLivingStoryMutation.isPending
                  }
                  onChange={(e) => {
                    toggleLivingStoryMutation.mutate({
                      documentId: row.original.id.toString(),
                      includeDetailsInLivingStory: e.currentTarget.checked
                    });
                  }}
                />
                <Text size="sm">Include</Text>
              </Group>
            )
          }),
          columnHelper.display({
            id: "actions",
            header: "Action",
            meta: {
              style: { verticalAlign: "top", textAlign: "center", width: "1%" }
            },
            cell: ({ row }) => {
              const doc = row.original;
              if (!doc.fileName) {
                return null;
              }
              const isLoading = downloadingId === doc.id;
              return (
                <Tooltip label={isLoading ? "Downloading..." : "Download file"}>
                  <ActionIcon
                    color="blue"
                    variant="subtle"
                    onClick={() => void handleDownload(doc)}
                    aria-label="Download file"
                    disabled={!!downloadingId}
                  >
                    {isLoading ? (
                      <Loader size={18} color="blue" />
                    ) : (
                      <IconDownload size={18} />
                    )}
                  </ActionIcon>
                </Tooltip>
              );
            }
          })
        ]}
      />
      {/* Outlet renders the various modals when a document or note is selected or created */}
      <Outlet />
    </>
  );
}
